import { useState, useEffect } from "react";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import RecurrenceBuilder, { DEFAULT_RECURRENCE } from "@/components/RecurrenceBuilder";
import ChannelPicker from "@/components/ChannelPicker";
import api, { formatApiError } from "@/lib/api";
import { BUSINESS_CATEGORIES } from "@/lib/bizCategories";

const SNAP_CATS = [
  ["housing", "Housing"], ["utilities", "Utilities"], ["food", "Food & Groceries"],
  ["transportation", "Transportation"], ["insurance", "Insurance"], ["healthcare", "Healthcare"],
  ["debt", "Debt Payments"], ["savings", "Savings"], ["entertainment", "Entertainment"],
  ["personal_care", "Personal Care"], ["children_pets", "Children & Pets"], ["miscellaneous", "Miscellaneous"],
];

const empty = () => ({
  title: "",
  notes: "",
  kind: "task",
  category: "personal",
  amount: "",
  snap_category: "",
  biz_category: "",
  time: "09:00",
  start_date: new Date().toISOString().slice(0, 10),
  recurrence: { ...DEFAULT_RECURRENCE },
  channels: ["email"],
  voice_script: "",
  address: "",
});

export default function TaskDialog({ open, onClose, onSaved, task, initialKind }) {
  const [form, setForm] = useState(empty());
  const [busy, setBusy] = useState(false);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/voice-templates").then((r) => setTemplates(r.data)).catch(() => {});
    if (task) {
      setForm({ ...empty(), ...task, amount: task.amount ?? "" });
    } else {
      api.get("/profile")
        .then((r) => setForm({ ...empty(), kind: initialKind || "task", channels: r.data.default_silent ? [] : ["email"] }))
        .catch(() => setForm({ ...empty(), kind: initialKind || "task" }));
    }
  }, [open, task, initialKind]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Please enter a title");
    setBusy(true);
    try {
      const payload = {
        ...form,
        amount: form.kind === "bill" ? parseFloat(form.amount) || 0 : null,
      };
      if (task) await api.put(`/tasks/${task.id}`, payload);
      else await api.post("/tasks", payload);
      toast.success(task ? "Reminder updated" : "Reminder created");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logPaidNow = async () => {
    if (!form.title.trim()) return toast.error("Please enter a title");
    const amt = parseFloat(form.amount) || 0;
    if (amt <= 0) return toast.error("Please enter an amount");
    setBusy(true);
    try {
      await api.post("/snapbudget/log-bill", {
        title: form.title, amount: amt, category: form.category,
        snap_category: form.snap_category, biz_category: form.biz_category,
        date: new Date().toISOString().slice(0, 10),
      });
      toast.success("Logged as paid in SnapBudget");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} testid="task-dialog" subtitle="Personal" title={task ? "Edit reminder" : "New reminder"}>
      <div className="space-y-4">
        <div className="flex gap-2">
          {[
            { v: "task", l: "To-Do / Reminder" },
            { v: "bill", l: "Bill Payment" },
          ].map((o) => (
            <button
              key={o.v}
              data-testid={`kind-${o.v}`}
              onClick={() => set("kind", o.v)}
              className={`flex-1 h-10 text-sm border transition-colors ${
                form.kind === o.v ? "bg-[#1A1A18] text-white border-[#1A1A18]" : "bg-white border-[var(--line)] hover:border-[#1A1A18]"
              }`}
            >
              {o.l}
            </button>
          ))}
        </div>

        <div>
          <span className="label-mono text-[var(--text-2)]">Category</span>
          <div className="flex gap-2 mt-2">
            {[
              { v: "personal", l: "Personal" },
              { v: "business", l: "Business" },
            ].map((o) => (
              <button
                key={o.v}
                data-testid={`category-${o.v}`}
                onClick={() => set("category", o.v)}
                className={`flex-1 h-10 text-sm border transition-colors ${
                  form.category === o.v
                    ? (o.v === "business" ? "bg-[#FFC43D] text-[#1A1A18] border-[#FFC43D]" : "bg-[#2563EB] text-white border-[#2563EB]")
                    : "bg-white border-[var(--line)] hover:border-[#1A1A18]"
                }`}
              >
                {o.l}
              </button>
            ))}
          </div>
          {form.category === "business" && (
            <p className="mt-2 text-xs text-[var(--text-2)]">Shows in the Business Day Planner and as a yellow dot on the Pulse calendar.</p>
          )}
        </div>

        <div>
          <span className="label-mono text-[var(--text-2)]">Title</span>
          <input
            data-testid="task-title"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder={form.kind === "bill" ? "Car Insurance" : "Wake Up Call"}
            className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white text-[var(--text)] placeholder:text-[var(--text-2)] outline-none focus:border-[var(--accent)]"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className="label-mono text-[var(--text-2)]">Time</span>
            <input
              type="time"
              data-testid="task-time"
              value={form.time}
              onChange={(e) => set("time", e.target.value)}
              className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white text-[var(--text)] placeholder:text-[var(--text-2)] outline-none focus:border-[var(--accent)]"
            />
          </div>
          {form.kind === "bill" ? (
            <div>
              <span className="label-mono text-[var(--text-2)]">Amount ($)</span>
              <input
                type="number"
                step="0.01"
                data-testid="task-amount"
                value={form.amount}
                onChange={(e) => set("amount", e.target.value)}
                className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white text-[var(--text)] placeholder:text-[var(--text-2)] outline-none focus:border-[var(--accent)]"
              />
            </div>
          ) : (
            <div>
              <span className="label-mono text-[var(--text-2)]">Start date</span>
              <input
                type="date"
                data-testid="task-start"
                value={form.start_date}
                onChange={(e) => set("start_date", e.target.value)}
                className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white text-[var(--text)] placeholder:text-[var(--text-2)] outline-none focus:border-[var(--accent)]"
              />
            </div>
          )}
        </div>

        <RecurrenceBuilder value={form.recurrence} onChange={(r) => set("recurrence", r)} />
        <ChannelPicker value={form.channels} onChange={(c) => set("channels", c)} />

        {form.kind === "bill" && (
          <div>
            <span className="label-mono text-[var(--text-2)]">{form.category === "business" ? "Business category" : "SnapBudget category"}</span>
            {form.category === "business" ? (
              <select
                data-testid="task-biz-category"
                value={form.biz_category || ""}
                onChange={(e) => set("biz_category", e.target.value)}
                className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white text-[var(--text)] outline-none focus:border-[var(--accent)]"
              >
                <option value="">Choose a business category…</option>
                {BUSINESS_CATEGORIES.map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            ) : (
              <select
                data-testid="task-snap-category"
                value={form.snap_category || ""}
                onChange={(e) => set("snap_category", e.target.value)}
                className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white text-[var(--text)] outline-none focus:border-[var(--accent)]"
              >
                <option value="">Choose a category…</option>
                {SNAP_CATS.map(([k, l]) => (<option key={k} value={k}>{l}</option>))}
              </select>
            )}
            <p className="mt-2 text-xs text-[var(--text-2)]">When you mark this bill paid, it auto-posts to {form.category === "business" ? "Business" : "Personal"} SnapBudget as a spent expense.</p>
          </div>
        )}

        <div>
          <span className="label-mono text-[var(--text-2)]">Message template (optional)</span>
          <select
            data-testid="task-template"
            value=""
            onChange={(e) => { const t = templates.find((x) => x.id === e.target.value); if (t) set("voice_script", t.text); }}
            className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white text-[var(--text)] placeholder:text-[var(--text-2)] outline-none focus:border-[var(--accent)]"
          >
            <option value="">Choose a template to fill the message…</option>
            {templates.map((t) => (<option key={t.id} value={t.id}>{t.name} · {t.category}</option>))}
          </select>
        </div>

        <div>
          <span className="label-mono text-[var(--text-2)]">Message sent (optional)</span>
          <textarea
            data-testid="task-message"
            value={form.voice_script || ""}
            onChange={(e) => set("voice_script", e.target.value)}
            rows={2}
            placeholder="Leave blank to send: Reminder: <title>"
            className="mt-2 w-full px-3 py-2 border border-[var(--line)] bg-white text-[var(--text)] placeholder:text-[var(--text-2)] outline-none focus:border-[var(--accent)]"
          />
        </div>

        <div>
          <span className="label-mono text-[var(--text-2)]">Notes</span>
          <textarea
            data-testid="task-notes"
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            rows={2}
            className="mt-2 w-full px-3 py-2 border border-[var(--line)] bg-white text-[var(--text)] placeholder:text-[var(--text-2)] outline-none focus:border-[var(--accent)]"
          />
        </div>

        <div>
          <span className="label-mono text-[var(--text-2)]">Address (optional)</span>
          <input
            data-testid="task-address"
            value={form.address || ""}
            onChange={(e) => set("address", e.target.value)}
            placeholder="123 Main St, Miami FL"
            className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white text-[var(--text)] placeholder:text-[var(--text-2)] outline-none focus:border-[var(--accent)]"
          />
        </div>

        <div className="flex gap-3 pt-2">
          <button data-testid="task-cancel" onClick={onClose} className="flex-1 h-11 border border-[var(--line)] hover:bg-white transition-colors">
            Cancel
          </button>
          <button
            data-testid="task-save"
            onClick={save}
            disabled={busy}
            className="flex-1 h-11 bg-[var(--accent)] text-white hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save reminder"}
          </button>
        </div>
        {form.kind === "bill" && (
          <button
            data-testid="task-log-paid"
            onClick={logPaidNow}
            disabled={busy}
            className="w-full h-11 border border-[#16a34a] text-[#16a34a] hover:bg-[#16a34a] hover:text-white transition-colors disabled:opacity-50"
          >
            Log as paid now → SnapBudget
          </button>
        )}
      </div>
    </Modal>
  );
}
